import { WatchSource, WatchStopHandle, watch } from 'vue';
import { DirectionsLineString } from '../../utils/models/navigation/routeDirections';

/** What the store holds for a route, as far as its turns are concerned. */
interface StoredRoute {
  lineStrings?: Array<{
    lineStringGeometry?: { coordinates?: number[][] };
    lineStringProperties?: { instructions?: DirectionsLineString['instructions']; profile?: string };
  }>;
}

/** A route's line strings, in the shape the directions reader wants. */
export function directionsLineStringsOf(route: StoredRoute | null | undefined): DirectionsLineString[] {
  return (route?.lineStrings ?? []).map((ls) => ({
    coordinates: ls.lineStringGeometry?.coordinates ?? [],
    instructions: ls.lineStringProperties?.instructions ?? [],
    profile: ls.lineStringProperties?.profile
  }));
}

/**
 * Rebuild the turn list whenever what it is built from changes, and only while it is open.
 *
 * `source` is the reactive read of the line strings themselves — `directionsLineStringsOf`
 * inside a `computed`. That read registers every segment, its geometry and its turns as a
 * dependency, so replacing a segment, re-wording a turn or switching routes all rebuild, and a
 * change that cannot move a turn (a marker's icon, a rename) does not.
 *
 * It used to watch a stamp of the route instead, with `updated_at` in a template string — and
 * a Date prints to the second. Dragging a marker commits straight provisional lines, then the
 * snapped line replaces them a few hundred milliseconds later; both printed the same second,
 * so the list stayed on the straight lines, turns missing and distances wrong, until it was
 * closed and opened again.
 */
export function rebuildWhileOpen(
  source: WatchSource<DirectionsLineString[]>,
  isOpen: () => boolean,
  rebuild: () => void
): WatchStopHandle {
  return watch(source, () => {
    if (isOpen()) rebuild();
  });
}
